import { useId, useLayoutEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/cn";
import { ResultsScroller } from "./ResultsScroller";
import { Surface } from "./Surface";
import { Text } from "./Text";
import { useCreationWorkspaceLayout } from "./CreationWorkspaceLayout";
import { useBrowserWorkspaceChrome } from "./BrowserWorkspaceChrome";
import { TopbarActions } from "./TopbarSlot";

export interface CreationResultsPanelProps {
  title: ReactNode;
  /** Keeps the heading for assistive technology when navigation labels the view. */
  titleHidden?: boolean;
  headingID: string;
  /** Optional control rendered on the same header baseline as the title. */
  leading?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  tone?: "canvas" | "glass";
  /** Only actual sources, documents and results replace the shared gallery. */
  workspaceDetail?: boolean;
  /** Browsing pages place these actions alongside their route title. */
  headerInTopbar?: boolean;
  loading?: boolean;
  scrollKey?: string;
}

/** Shared results shell for image, video, and audio creation workspaces. */
export function CreationResultsPanel({
  title,
  titleHidden = false,
  headingID,
  leading,
  actions,
  children,
  className,
  bodyClassName,
  tone = "canvas",
  workspaceDetail = false,
  headerInTopbar = false,
  loading = false,
  scrollKey,
}: CreationResultsPanelProps) {
  const workspace = useCreationWorkspaceLayout();
  const browserChrome = useBrowserWorkspaceChrome();
  const integratedHeader = headerInTopbar && Boolean(browserChrome);
  const detailID = useId();
  const registerDetail = workspace?.registerDetail;
  useLayoutEffect(() => registerDetail?.(detailID, workspaceDetail), [detailID, registerDetail, workspaceDetail]);
  if (workspace) {
    if (!workspaceDetail || !workspace.resultsTarget) return null;
    return createPortal(
      <div className="studio-creation-detail min-w-0">
        {actions ? <div className="mb-3 flex flex-wrap items-center justify-end gap-2">{actions}</div> : null}
        {children}
      </div>,
      workspace.resultsTarget,
      detailID,
    );
  }
  return (
    <Surface
      as="section"
      tone={tone}
      border="none"
      radius="none"
      aria-labelledby={headingID}
      className={cn(
        "studio-results min-h-[var(--composer-panel-height,var(--workspace-content-height,calc(100dvh-56px)))] min-w-0 overflow-hidden",
        tone === "glass" ? "studio-overlay" : "bg-[var(--creation-results-surface)]",
        className,
      )}
    >
      {integratedHeader ? <>
        <h2 id={headingID} className="sr-only">{title}</h2>
        <TopbarActions>{actions}</TopbarActions>
      </> : <header className="studio-results-header flex min-h-14 flex-wrap items-center justify-between gap-2 px-4 py-1.5 sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          {leading ? <div className="studio-results-leading shrink-0">{leading}</div> : null}
          <Text
            id={headingID}
            as="h2"
            variant="ui"
            className={cn("text-[14px] font-semibold", titleHidden && "sr-only")}
          >
            {title}
          </Text>
        </div>
        {actions}
      </header>}
      {integratedHeader ? <ResultsScroller loading={loading} resetKey={scrollKey} className={cn("px-4 pb-4 sm:px-5 sm:pb-5", bodyClassName)}>
        {children}
      </ResultsScroller> : <div className={cn("px-4 pb-4 sm:px-5 sm:pb-5", bodyClassName)}>
        {children}
      </div>}
    </Surface>
  );
}
